if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[name="add"]');
        this.speedIntakeFlow = this.dataset.speedIntakeFlow === 'true';
        this.speedCheckoutRedirect = this.dataset.speedCheckoutRedirect === 'true';

        if (document.querySelector('cart-drawer') && this.submitButton)
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupSpeedIntakeFlow();
      }

      setupSpeedIntakeFlow() {
        if (!this.speedIntakeFlow) return;

        this.sectionId = this.dataset.sectionId;
        this.modal = document.getElementById(`SpeedIntakeModal-${this.sectionId}`);
        if (!this.modal) return;

        this.openModalButton = this.querySelector('[data-open-speed-intake-modal]');
        this.closeModalButtons = this.modal.querySelectorAll('[data-speed-intake-close]');
        this.continueButton = this.modal.querySelector('[data-speed-intake-continue]');
        this.brandInput = this.modal.querySelector('[data-speed-intake-input="brand"]');
        this.websiteInput = this.modal.querySelector('[data-speed-intake-input="website"]');
        this.shopifyInput = this.modal.querySelector('[data-speed-intake-input="shopify"]');
        this.deliveryDateNode = this.modal.querySelector('[data-speed-intake-delivery-date]');
        this.subscriptionSummaryRow = this.modal.querySelector('[data-speed-intake-subscription-row]');
        this.subscriptionSummaryLabel = this.modal.querySelector('[data-speed-intake-subscription-label]');
        this.maintenanceIncludeItem = this.modal.querySelector('[data-speed-intake-maintenance-include]');

        this.hiddenBrand = this.form.querySelector('.speed-intake-hidden-brand');
        this.hiddenWebsite = this.form.querySelector('.speed-intake-hidden-website');
        this.hiddenShopify = this.form.querySelector('.speed-intake-hidden-shopify');
        this.hiddenProtection = this.form.querySelector('.speed-intake-hidden-protection');
        this.hiddenSellingPlanInput = this.form.querySelector('input[type="hidden"][name="selling_plan"]');
        this.initialHiddenSellingPlanValue = this.hiddenSellingPlanInput
          ? (this.hiddenSellingPlanInput.value || '').trim()
          : '';
        this.purchaseOptionInteracted = false;
        this.lastPurchaseIntent = 'unknown';

        if (this.openModalButton) {
          this.openModalButton.addEventListener('click', () => this.openSpeedIntakeModal());
        }

        this._onSpeedIntakeCloseTrigger = (event) => {
          const target = event.target instanceof Element ? event.target.closest('[data-speed-intake-close]') : null;
          if (!target || !this.modal.contains(target)) return;
          event.preventDefault();
          event.stopPropagation();
          this.closeSpeedIntakeModal();
        };

        this.closeModalButtons.forEach((button) => {
          button.addEventListener('click', this._onSpeedIntakeCloseTrigger);
        });

        // Delegated fallback in case app/theme scripts re-render modal content.
        // Capture phase avoids lost clicks when app scripts stop propagation.
        this.modal.addEventListener('click', this._onSpeedIntakeCloseTrigger, true);

        [this.brandInput, this.websiteInput, this.shopifyInput].forEach((input) => {
          if (input) input.addEventListener('input', () => this.updateSpeedContinueState());
          if (input) input.addEventListener('change', () => this.updateSpeedContinueState());
        });

        const onSellingPlanFieldUpdated = () => {
          this.updateSpeedIntakeSummary();
          this.syncSpeedMaintenanceToggleFromSellingPlan();
        };
        this.form.querySelectorAll('[name="selling_plan"]').forEach((input) => {
          input.addEventListener('change', onSellingPlanFieldUpdated);
          input.addEventListener('input', onSellingPlanFieldUpdated);
        });

        this._sellingPlanDocListener = (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.getAttribute('form') !== this.form.id) return;
          if (!target.matches('[name="selling_plan"], [name^="selling_plan_"]')) return;
          window.setTimeout(() => onSellingPlanFieldUpdated(), 0);
        };
        document.addEventListener('change', this._sellingPlanDocListener);
        document.addEventListener('input', this._sellingPlanDocListener);

        this.bindPurchaseOptionSignals();

        if (this.continueButton) {
          this.continueButton.addEventListener('click', () => this.submitSpeedIntakeForm());
        }

        this.updateSpeedIntakeTopOffset();
        this.populateSpeedIntakeDeliveryDate();

        window.addEventListener('resize', () => this.updateSpeedIntakeTopOffset());

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
            this.closeSpeedIntakeModal();
          }
        });

        queueMicrotask(() => this.setupSpeedMaintenanceProtectionToggle());
      }

      disconnectedCallback() {
        if (this._sellingPlanDocListener) {
          document.removeEventListener('change', this._sellingPlanDocListener);
          document.removeEventListener('input', this._sellingPlanDocListener);
        }
        if (this._onSpeedIntakeCloseTrigger && this.closeModalButtons && this.closeModalButtons.length) {
          this.closeModalButtons.forEach((button) => {
            button.removeEventListener('click', this._onSpeedIntakeCloseTrigger);
          });
        }
        if (this._onSpeedIntakeCloseTrigger && this.modal) {
          this.modal.removeEventListener('click', this._onSpeedIntakeCloseTrigger, true);
        }
        if (this.maintenanceToggle && this._onMaintenanceToggleChange) {
          this.maintenanceToggle.removeEventListener('change', this._onMaintenanceToggleChange);
        }
        if (this._speedMainProductRoot && this._maintenanceDelegatedChange) {
          this._speedMainProductRoot.removeEventListener('change', this._maintenanceDelegatedChange);
          this._maintenanceDelegatedChange = null;
        }
        this._speedMaintenanceProtectionBound = false;
      }

      openSpeedIntakeModal() {
        if (!this.modal) return;
        /* Move off-screen modal to <body> so position:fixed is not trapped by transformed ancestors (scroll animations, apps, etc.). */
        if (this.modal.parentElement !== document.body) {
          document.body.appendChild(this.modal);
        }
        this.updateSpeedIntakeTopOffset();
        this.modal.removeAttribute('hidden');
        /* Apply immediately: while .is-open is missing, .speed-intake-modal uses pointer-events:none and
           descendants inherit it, so inputs/checkboxes are unclickable until the next frame. */
        this.modal.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        document.body.classList.add('speed-intake-open');
        this.populateSpeedIntakeDeliveryDate();
        this.updateSpeedIntakeSummary();
        this.syncSpeedMaintenanceToggleFromSellingPlan();
        this.updateSpeedContinueState();
      }

      closeSpeedIntakeModal() {
        if (!this.modal) return;
        this.modal.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        document.body.classList.remove('speed-intake-open');
        window.setTimeout(() => {
          if (!this.modal.classList.contains('is-open')) {
            this.modal.setAttribute('hidden', '');
          }
        }, 260);
      }

      getSelectedSellingPlanLabel() {
        if (!this.form) return '';

        /* Radio group: empty value = one-time; do not fall through to hidden fields (stale value). */
        const checkedPlanRadio = this.form.querySelector('input[type="radio"][name="selling_plan"]:checked');
        if (checkedPlanRadio) {
          const val = (checkedPlanRadio.value || '').trim();
          if (!val) return '';

          const explicitName = checkedPlanRadio.dataset.sellingPlanName || checkedPlanRadio.dataset.planName;
          if (explicitName && explicitName.trim() !== '') return explicitName.trim();

          const rid = checkedPlanRadio.id;
          const esc =
            rid && typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(rid) : rid;
          const labelByFor = rid ? this.form.querySelector(`label[for="${esc}"]`) : null;
          const wrappingLabel = checkedPlanRadio.closest('label');
          const rawText = (labelByFor || wrappingLabel)?.textContent || '';
          const parsed = rawText.replace(/\s+/g, ' ').trim();
          return parsed || 'Selected';
        }

        const selectInput = this.form.querySelector('select[name="selling_plan"]');
        if (selectInput && selectInput.value) {
          const selectedOption = selectInput.options[selectInput.selectedIndex];
          const rawText = selectedOption ? selectedOption.textContent : '';
          const parsed = (rawText || '').replace(/\s+/g, ' ').trim();
          return parsed || 'Selected';
        }

        const hiddenInput = this.form.querySelector('input[type="hidden"][name="selling_plan"]');
        if (hiddenInput && hiddenInput.value) {
          const currentHiddenValue = (hiddenInput.value || '').trim();
          const purchaseState = this.getPurchaseOptionState();
          if (purchaseState === 'subscription') return 'Selected';
          if (purchaseState === 'one_time') return '';

          if (this.lastPurchaseIntent === 'subscription') return 'Selected';
          if (this.lastPurchaseIntent === 'one_time') return '';

          // Some subscription apps keep a hidden selling_plan populated by default.
          // Treat it as selected only when it changes from initial load.
          if (currentHiddenValue !== this.initialHiddenSellingPlanValue) return 'Selected';
          if (this.purchaseOptionInteracted && currentHiddenValue !== '') return 'Selected';
          if (this.initialHiddenSellingPlanValue === '' && currentHiddenValue !== '') return 'Selected';
          return '';
        }

        return '';
      }

      getPurchaseOptionState() {
        if (!this.form) return 'unknown';

        const texts = this.getSelectedPurchaseChoiceTexts().map((text) => text.toLowerCase());

        if (
          texts.some(
            (text) =>
              text.includes('one time') ||
              text.includes('one-time') ||
              text.includes('one time purchase') ||
              text.includes('single purchase') ||
              text.includes('buy once') ||
              text.includes('one off')
          )
        ) {
          return 'one_time';
        }
        if (
          texts.some(
            (text) =>
              text.includes('subscri') ||
              text.includes('monthly') ||
              text.includes('every month') ||
              text.includes('delivery every') ||
              text.includes('auto renew')
          )
        ) {
          return 'subscription';
        }
        return 'unknown';
      }

      getSelectedPurchaseChoiceTexts() {
        if (!this.form) return [];

        const scope = this.form.closest('.product__info-container') || this.form;

        const selectedInputs = Array.from(
          scope.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')
        ).filter((input) => input.name !== 'selling_plan' && input.dataset.speedIntakeInput == null);

        const inputTexts = selectedInputs
          .map((input) => {
            const labelByFor = input.id ? scope.querySelector(`label[for="${input.id}"]`) : null;
            const wrappingLabel = input.closest('label');
            const rawText = (labelByFor || wrappingLabel)?.textContent || '';
            return rawText.replace(/\s+/g, ' ').trim();
          })
          .filter(Boolean);

        const selectedDropdowns = Array.from(scope.querySelectorAll('select')).filter(
          (select) => select.name !== 'selling_plan' && select.selectedIndex >= 0
        );
        const selectTexts = selectedDropdowns
          .map((select) => {
            const option = select.options[select.selectedIndex];
            return option ? option.textContent.replace(/\s+/g, ' ').trim() : '';
          })
          .filter(Boolean);

        const selectedSemanticNodes = Array.from(
          scope.querySelectorAll(
            '[role="radio"][aria-checked="true"], [aria-selected="true"], [aria-pressed="true"], [data-state="selected"], [data-state="checked"]'
          )
        );
        const semanticTexts = selectedSemanticNodes
          .map((node) => (node.textContent || '').replace(/\s+/g, ' ').trim())
          .filter((text) => text.length > 0 && text.length < 140);

        return [...inputTexts, ...selectTexts, ...semanticTexts];
      }

      bindPurchaseOptionSignals() {
        const scope = this.form.closest('.product__info-container') || this.form;
        if (!scope) return;

        const purchaseSelectors = [
          'input[name="selling_plan"]',
          'select[name="selling_plan"]',
          'input[name*="selling_plan"]',
          'select[name*="selling_plan"]',
          '[role="radio"]',
          '[aria-checked]',
          '[data-state="selected"]',
          '[data-state="checked"]',
          '[data-selling-plan]',
          '[data-purchase-option]',
          '[class*="selling-plan"]',
          '[class*="subscription"]',
          '[for*="selling_plan"]',
          '[id*="selling_plan"]',
        ].join(', ');

        const signalHandler = (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          /* Avoid treating Speed maintenance copy / checkbox as subscription-app UI (broad selectors). */
          if (target.closest('[data-speed-maintenance-card]')) return;
          if (!target.closest(purchaseSelectors)) return;

          this.purchaseOptionInteracted = true;
          const signalText = this.extractPurchaseSignalText(target, scope);
          const intent = this.derivePurchaseIntent(signalText);
          if (intent !== 'unknown') this.lastPurchaseIntent = intent;

          window.setTimeout(() => {
            this.updateSpeedIntakeSummary();
            this.syncSpeedMaintenanceToggleFromSellingPlan();
          }, 60);
        };

        scope.addEventListener('change', signalHandler, true);
        scope.addEventListener('click', signalHandler, true);
      }

      extractPurchaseSignalText(target, scope) {
        const labelByFor = target.id ? scope.querySelector(`label[for="${target.id}"]`) : null;
        const wrappingLabel = target.closest('label');
        const contextualNode =
          target.closest('[role="radio"], [data-purchase-option], [data-selling-plan], .subscription, .selling-plan') ||
          target.closest('button') ||
          target;
        const rawText =
          (labelByFor && labelByFor.textContent) ||
          (wrappingLabel && wrappingLabel.textContent) ||
          (contextualNode && contextualNode.textContent) ||
          '';
        return rawText.replace(/\s+/g, ' ').trim();
      }

      derivePurchaseIntent(text) {
        const normalized = (text || '').toLowerCase();
        if (
          normalized.includes('one time') ||
          normalized.includes('one-time') ||
          normalized.includes('single purchase') ||
          normalized.includes('buy once') ||
          normalized.includes('one off')
        ) {
          return 'one_time';
        }
        if (
          normalized.includes('subscri') ||
          normalized.includes('monthly') ||
          normalized.includes('every month') ||
          normalized.includes('delivery every') ||
          normalized.includes('auto renew')
        ) {
          return 'subscription';
        }
        return 'unknown';
      }

      updateSpeedIntakeSummary() {
        this.refreshSpeedMaintenanceElements();
        const sellingPlanLabel = this.getSelectedSellingPlanLabel();
        const maintenanceOn = Boolean(this.maintenanceToggle?.checked);
        const hasProtection = Boolean(sellingPlanLabel) || maintenanceOn;

        if (this.subscriptionSummaryLabel) {
          this.subscriptionSummaryLabel.textContent = hasProtection
            ? 'Protection Added'
            : 'Protection not added';
          this.subscriptionSummaryLabel.classList.toggle(
            'speed-intake-modal__summary-status--active',
            hasProtection
          );
          this.subscriptionSummaryLabel.classList.toggle(
            'speed-intake-modal__summary-status--inactive',
            !hasProtection
          );
        }

        if (this.maintenanceIncludeItem) {
          if (hasProtection) {
            this.maintenanceIncludeItem.removeAttribute('hidden');
          } else {
            this.maintenanceIncludeItem.setAttribute('hidden', '');
          }
        }
      }

      updateSpeedContinueState() {
        if (!this.continueButton) return;
        const valid =
          this.brandInput &&
          this.websiteInput &&
          this.shopifyInput &&
          this.brandInput.value.trim() !== '' &&
          this.websiteInput.value.trim() !== '' &&
          this.shopifyInput.checked;
        this.continueButton.disabled = !valid;
      }

      isSpeedProtectionEnabled() {
        this.refreshSpeedMaintenanceElements();
        return Boolean(this.maintenanceToggle?.checked || this.getSelectedSellingPlanLabel());
      }

      submitSpeedIntakeForm() {
        if (!this.brandInput || !this.websiteInput || !this.shopifyInput) return;
        if (!this.hiddenBrand || !this.hiddenWebsite || !this.hiddenShopify) return;

        const brand = this.brandInput.value.trim();
        const website = this.websiteInput.value.trim();
        const isShopify = this.shopifyInput.checked;
        if (!brand || !website || !isShopify) return;

        this.hiddenBrand.value = brand;
        this.hiddenWebsite.value = website;
        this.hiddenShopify.value = 'Yes';
        if (this.hiddenProtection) {
          this.hiddenProtection.value = this.isSpeedProtectionEnabled() ? 'Yes' : 'No';
        }
        this.closeSpeedIntakeModal();
        let submitted = false;
        if (this.form && typeof this.form.requestSubmit === 'function') {
          try {
            this.form.requestSubmit();
            submitted = true;
          } catch (error) {
            console.warn('Speed intake requestSubmit fallback triggered', error);
          }
        }
        if (!submitted) {
          this.onSubmitHandler({ preventDefault: () => {} });
        }
      }

      populateSpeedIntakeDeliveryDate() {
        if (!this.deliveryDateNode) return;

        const shouldShiftWeekend = this.deliveryDateNode.dataset.weekendShift !== 'false';
        const etaDate = new Date();
        etaDate.setDate(etaDate.getDate() + 14);

        if (shouldShiftWeekend) {
          const day = etaDate.getDay();
          if (day === 6) {
            etaDate.setDate(etaDate.getDate() + 2);
          } else if (day === 0) {
            etaDate.setDate(etaDate.getDate() + 1);
          }
        }

        const month = etaDate.toLocaleString('en-US', { month: 'short' });
        const dayOfMonth = String(etaDate.getDate()).padStart(2, '0');
        this.deliveryDateNode.textContent = `Week of ${month} ${dayOfMonth}`;
      }

      updateSpeedIntakeTopOffset() {
        if (!this.modal) return;
        const headerSection = document.querySelector('.section-header');
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        let offset = isMobile ? 16 : 72;
        if (headerSection) {
          const rect = headerSection.getBoundingClientRect();
          if (rect.height > 0) {
            offset = Math.max(0, Math.round(rect.bottom));
          }
        }
        if (isMobile) {
          // Keep the modal near the top on mobile even when sticky headers are tall.
          offset = Math.min(offset, 18);
        }
        this.modal.style.setProperty('--speed-intake-top-offset', `${offset}px`);
      }

      setupSpeedMaintenanceProtectionToggle() {
        if (this._speedMaintenanceProtectionBound) return;
        this._speedMaintenanceProtectionBound = true;
        this._syncingMaintenanceToggle = false;
        this._speedMainProductRoot = this.closest(`#MainProduct-${this.dataset.sectionId}`);
        this.refreshSpeedMaintenanceElements();

        this._onMaintenanceToggleChange = () => {
          if (this._syncingMaintenanceToggle) return;
          this.refreshSpeedMaintenanceElements();
          if (!this.maintenanceToggle) return;
          this.purchaseOptionInteracted = true;
          this.lastPurchaseIntent = this.maintenanceToggle.checked ? 'subscription' : 'one_time';
          this.applySpeedMaintenanceProtectionToggle(this.maintenanceToggle.checked);
          this.updateSpeedIntakeSummary();
          window.requestAnimationFrame(() => this.syncSpeedMaintenanceToggleFromSellingPlan());
        };

        /*
          The protection checkbox is rendered in the main-product section (Speed maintenance block), not inside
          <product-form>. Listen on the section root so `closest('#MainProduct-…')` works after `connectedCallback`
          timing and clicks on the label still hit `change` on the input.
        */
        const root = this._speedMainProductRoot;
        if (root) {
          this._maintenanceDelegatedChange = (event) => {
            const t = event.target;
            if (!(t instanceof Element) || !t.matches('[data-speed-maintenance-protection-toggle]')) return;
            if (!root.contains(t)) return;
            const pf = root.querySelector('product-form[data-speed-intake-flow="true"]');
            if (pf !== this) return;
            this._onMaintenanceToggleChange();
          };
          root.addEventListener('change', this._maintenanceDelegatedChange);
        } else if (this.maintenanceToggle) {
          this.maintenanceToggle.addEventListener('change', this._onMaintenanceToggleChange);
        }

        window.setTimeout(() => this.syncSpeedMaintenanceToggleFromSellingPlan(), 0);
        window.setTimeout(() => this.syncSpeedMaintenanceToggleFromSellingPlan(), 800);
      }

      refreshSpeedMaintenanceElements() {
        const root = this._speedMainProductRoot || this.closest(`#MainProduct-${this.dataset.sectionId}`);
        this.maintenanceCard = root
          ? root.querySelector('[data-speed-maintenance-card]')
          : document.querySelector('[data-speed-maintenance-card]');
        this.maintenanceToggle = this.maintenanceCard?.querySelector('[data-speed-maintenance-protection-toggle]');
      }

      syncSpeedMaintenanceToggleFromSellingPlan() {
        this.refreshSpeedMaintenanceElements();
        if (!this.maintenanceToggle) return;
        const want = Boolean(this.getSelectedSellingPlanLabel());
        if (this.maintenanceToggle.checked === want) return;
        // Do not clear the protection checkbox while the user chose subscription but
        // selling_plan UI has not caught up yet (e.g. bridge radios not in DOM or plan id missing).
        if (!want && this.maintenanceToggle.checked && this.lastPurchaseIntent === 'subscription') return;
        this._syncingMaintenanceToggle = true;
        this.maintenanceToggle.checked = want;
        this._syncingMaintenanceToggle = false;
      }

      getSellingPlanFormFields() {
        const form = this.form;
        if (!form) return [];
        return [
          ...document.querySelectorAll(
            'input[name="selling_plan"], input[name^="selling_plan_"], select[name="selling_plan"]'
          ),
        ].filter((el) => el.form === form);
      }

      ensureBridgeHiddenSellingPlanInput() {
        let el = this.form.querySelector('input[type="hidden"][name="selling_plan"][data-speed-bridge-hidden]');
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = 'selling_plan';
          el.setAttribute('data-speed-bridge-hidden', '');
          this.form.appendChild(el);
        }
        return el;
      }

      applySpeedMaintenanceProtectionToggle(wantProtection) {
        this.refreshSpeedMaintenanceElements();
        const planId = (this.maintenanceCard && this.maintenanceCard.dataset.speedSellingPlanId) || '';
        const fields = this.getSellingPlanFormFields();
        const selects = fields.filter((el) => el.tagName === 'SELECT');
        if (selects.length) {
          const sel = selects[0];
          const opts = [...sel.options];
          const emptyOpt = opts.find((o) => !String(o.value || '').trim());
          const planOpt = opts.find((o) => String(o.value || '').trim() !== '');
          const next = wantProtection ? planOpt || emptyOpt : emptyOpt || planOpt;
          if (!next) return;
          if (sel.value !== next.value) {
            sel.value = next.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        const radios = fields.filter((el) => el instanceof HTMLInputElement && el.type === 'radio');
        if (radios.length) {
          const offRadio = radios.find((r) => !String(r.value || '').trim());
          const onRadio = radios.find((r) => String(r.value || '').trim() !== '');
          const pick = wantProtection ? onRadio || radios[radios.length - 1] : offRadio || radios[0];
          if (pick && !pick.checked) {
            pick.checked = true;
            pick.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        let hiddens = fields.filter((el) => el instanceof HTMLInputElement && el.type === 'hidden' && el.name === 'selling_plan');
        if (!hiddens.length && planId) {
          hiddens = [this.ensureBridgeHiddenSellingPlanInput()];
        }
        if (!hiddens.length) return;
        const nextVal = wantProtection && planId ? String(planId) : '';
        hiddens.forEach((h) => {
          if (h.name !== 'selling_plan') return;
          if (h.value !== nextVal) {
            h.value = nextVal;
            h.dispatchEvent(new Event('input', { bubbles: true }));
            h.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      /**
       * Subscription apps often associate `selling_plan` inputs with this form via the `form=""` attribute.
       * Their hidden defaults can still submit a plan while the speed PDP summary shows "Protection not added",
       * which triggers Shopify: "Cannot apply selling plan to variant".
       */
      shouldOmitSellingPlanFromSpeedCartAdd() {
        if (!this.speedIntakeFlow) return false;
        if (this.getPurchaseOptionState() === 'subscription' || this.lastPurchaseIntent === 'subscription') {
          return false;
        }
        if (this.getSelectedSellingPlanLabel()) return false;
        return true;
      }

      omitSellingPlanFieldsFromFormData(formData) {
        const keys = [...formData.keys()];
        for (const key of keys) {
          if (key === 'selling_plan' || key.startsWith('selling_plan')) {
            formData.delete(key);
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.shouldOmitSellingPlanFromSpeedCartAdd()) {
          this.omitSellingPlanFieldsFromFormData(formData);
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (this.speedCheckoutRedirect) {
              window.location = (window.routes && window.routes.checkout_url) || '/checkout';
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
